/**
 * v0.4 §7 通知基盤: event_type_id ごとの日本語表示ラベル・本文レンダラー。
 * 文面は apps/api/src/lib/notifications/message-templates.ts (実際のメール/LINE配信文面) の
 * 言い回しを踏襲する (フロントは配信済み通知の一覧表示用に独自にレンダリングする、payload_json は
 * サーバの発火元と1対1のためキー形は必ず該当テンプレートと突き合わせること)。
 */

export type NotificationPayload = Record<string, unknown>;

// ⚠️ apps/api の lib/notifications/message-templates.ts と同じ修正 (design-and-code レビュー指摘):
// deviationCount/level/thresholdPercent 等の数値payloadがfallback ('') に落ち本文から数値が消える実害。
const str = (v: unknown, fallback = ''): string => {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return fallback;
};

export type RenderedNotification = { title: string; body: string };

type Renderer = (payload: NotificationPayload) => RenderedNotification;

const RENDERERS: Record<string, Renderer> = {
  // ⚠️ §8-55 / §8-93: API 側の apps/api/src/lib/notifications/message-templates.ts と対で更新すること。
  //    片側だけ足すと in_app 通知が「新しい通知があります (event_type_id)」の汎用フォールバックになり、
  //    payload (店舗名・達成率等) が捨てられる。
  // v0.4 追加要件⑭ (D-454)
  'kpi.target_shortfall': (p) => ({
    title: '売上が目標を下回りました',
    body: `${str(p.nodeName)} ${str(p.targetMonth)} の売上は目標比 ${str(p.achievementPercent)}% でした。`,
  }),
  'publish.period_published': (p) => ({
    title: 'ダッシュボードが公開されました',
    body: `${str(p.nodeName)} ${str(p.targetMonth)} 分の経営ダッシュボードが公開されました。`,
  }),
  'shift.period_finalized': (p) => ({
    title: 'シフトが確定しました',
    body: `${str(p.nodeName)} ${str(p.periodLabel)} (${str(p.startDate)}〜${str(p.endDate)}) のシフトが確定しました。`,
  }),
  'shift.draft_deadline': (p) => ({
    title: 'シフト提出締切が近づいています',
    body: `${str(p.nodeName)} ${str(p.periodLabel)} の提出締切は ${str(p.deadlineAt)} です。`,
  }),
  'shift.changed_after_confirm': (p) => ({
    title: '確定後シフトに変更がありました',
    body: `${str(p.nodeName)} ${str(p.periodLabel)} の確定済みシフトに変更がありました。${str(p.changeSummary)}`,
  }),
  'shift.urgent_request': (p) => ({
    title: '緊急の応援要請があります',
    body: `${str(p.targetDate)} の ${str(p.nodeName)} で人員が不足しています。理由: ${str(p.reason)}`,
  }),
  // v0.4 追加要件⑳ C (D-522/D-538)
  'shift.support_assigned': (p) => ({
    title: '応援勤務のお知らせ',
    body: `${str(p.workDate)} は ${str(p.nodeName)} への応援勤務です。`,
  }),
  'shift.standing_review': (p) => ({
    title: '恒常パターンの見直し提案',
    body: `${str(p.employeeName)} さんの恒常シフトからの逸脱が直近 ${str(p.deviationCount)} 回発生しています。`,
  }),
  'labor.agreement36_expiring': (p) => ({
    title: '36協定の有効期限が近づいています',
    body: `${str(p.nodeName)} の36協定は ${str(p.agreementValidTo)} に失効します。`,
  }),
  'approval.pending': (p) => ({
    // 使い勝手フィードバック⑤a (D-U43): 再提出の場合はタイトルに明示する。
    title: p.isResubmission === true ? '再提出された申請の承認待ちです' : '承認待ちの申請があります',
    body: `${str(p.workflowLabel)} (レベル${str(p.level)}): ${str(p.requestSummary)}`,
  }),
  // 使い勝手フィードバック⑤a (D-U27): 差し戻し (申請者まで戻す経路) の通知。
  // ⚠️ API側 (message-templates.ts) と同じ修正が必要 (§8-55/§8-93): 片側だけ足すと in_app 通知が
  // 汎用フォールバックになり理由が捨てられる。
  'approval.returned': (p) => ({
    title: '申請が差し戻されました',
    body: `${str(p.workflowLabel)} の申請が差し戻されました。理由: ${str(p.reason)}`,
  }),
  // 使い勝手フィードバック④ (D-U18): ゼロ段階承認 (max_levels=0) が成立した申請の watcher 向け通知。
  'approval.auto_approved': (p) => ({
    title: '申請が受理されました',
    body: `${str(p.workflowLabel)} の申請が (承認を経ずに) 受理されました。概要: ${str(p.requestSummary)}`,
  }),
  'ai.quota_warning': (p) => ({
    title: 'AI利用量が上限に近づいています',
    body: `${str(p.scopeLabel)} のAI利用量が上限の ${str(p.thresholdPercent)}% に達しました。`,
  }),
  // v0.4後半 月次実績一括入力 第2フェーズ §4-7 (D-1033): payloadに氏名・休暇種別・日付を含めない設計
  // (design書のprivacy方針)。kindのみで文面を出し分ける。
  'labor.leave_consumption_mismatch': (p) => ({
    title: '有休消化の記録に不一致があります',
    body: p.kind === 'orphan'
      ? '取消済みの勤務実績に休暇の消化記録が残ったままです。確認してください。'
      : '休暇を取得した勤務実績に消化記録がありません。確認してください。',
  }),
  // 月次実績 第2フェーズ I-5 (§7-4): API側 (lib/notifications/message-templates.ts) と対で更新。
  'work_record.returned': (p) => {
    const isAggregate = typeof p.periodLabel === 'string' && p.periodLabel.length > 0;
    return {
      title: '勤務実績が却下されました',
      body: isAggregate
        ? `${str(p.periodLabel)} の勤務実績 (${str(p.dayCount)}日分) が却下されました。理由: ${str(p.reason) || 'MiseSparkでご確認ください'}`
        : `${str(p.workDate)} の勤務実績が却下されました。理由: ${str(p.reason) || 'MiseSparkでご確認ください'}`,
    };
  },
  'work_record.reopened': (p) => ({
    title: '承認済みの勤務実績が差し戻されました',
    body: `${str(p.periodLabel)} の勤務実績 (${str(p.dayCount)}日分) が差し戻されました。理由: ${str(p.reason) || 'MiseSparkでご確認ください'}`,
  }),
  'work_record.amended': (p) => ({
    title: '勤務実績が修正されました',
    body: `${str(p.workDate)} の勤務実績が承認者により修正されました。変更履歴でご確認ください。`,
  }),
};

/** カタログに無い event_type_id は fail-safe として汎用文言にフォールバックする (サーバ側と同方針)。 */
export const renderNotification = (eventTypeId: string, payload: NotificationPayload): RenderedNotification => {
  const renderer = RENDERERS[eventTypeId];
  if (renderer) return renderer(payload);
  return { title: '通知があります', body: `新しい通知があります (${eventTypeId})` };
};

/** GET /me/notification-subscriptions が返す opt_out_allowed=1 のイベントのみが対象
 *  (migrations の notification_event_types seed と一致させること。
 *   ⚠️ 未登録だと購読トグルに event_type_id の生コードが表示される)。 */
export const SUBSCRIPTION_EVENT_LABELS: Record<string, string> = {
  'kpi.target_shortfall': '売上目標の未達通知',
  'publish.period_published': 'ダッシュボード公開通知',
  'shift.draft_deadline': 'シフト提出締切のリマインド',
  'shift.urgent_request': '緊急応援要請',
  'shift.standing_review': '恒常パターン見直し提案',
  'approval.pending': '承認待ち通知',
  'approval.auto_approved': '申請の受理通知 (ゼロ段階承認)',
  'ai.quota_warning': 'AI利用量の警告',
  'labor.leave_consumption_mismatch': '有休消化の記録不一致通知',
};
